import { Link } from '@tanstack/react-router';
import { Code2, Info, Zap } from 'lucide-react';
import {
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import {
  PlacementPricingType,
  PlacementStatus,
  PlacementType,
  resolvePlacementStatusLabel,
  resolvePlacementStatusTone,
  type Placement,
} from '../../model';
import { PlacementActions, PlacementIdCell } from './PlacementTableCells';
import { formatCurrency, formatDisplayDate, numberFormat } from '../../../../../lib/format';

export interface PlacementColumnsOptions {
  adUnitAllowed: boolean;
  canEdit: boolean;
  canArchive: boolean;
  canOpenEndpointGenerator: boolean;
  statusPendingIds: ReadonlySet<string>;
  onArchive: (placement: Placement) => void;
  onShowIntegrations: (placement: Placement) => void;
  onShowTargeting: (placement: Placement) => void;
  onShowUnitTag: (placement: Placement) => void;
}

export function buildPlacementColumns(options: PlacementColumnsOptions) {
  return buildDashboardDataTableColumns<Placement>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.name'),
      headerClassName: 'w-64 pl-4',
      className: 'w-64 pl-4 font-medium',
      sortable: true,
      sortKey: 'name',
      value: (placement) =>
        options.canEdit ? (
          <Link
            className={dashboardTableTextActionClassName()}
            to="/supply/placements/edit/$placementId"
            params={{ placementId: placement.id }}
          >
            {placement.name}
          </Link>
        ) : (
          placement.name
        ),
    },
    {
      id: 'type',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.type'),
      hidden: !options.adUnitAllowed,
      sortable: true,
      value: (placement) =>
        dashboardT(
          placement.type === PlacementType.AdUnit
            ? 'placements.type.adUnit'
            : 'placements.type.standard',
        ),
    },
    {
      id: 'unitTag',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.unitTag'),
      hidden: !options.adUnitAllowed,
      headerClassName: 'w-20 text-center',
      className: 'w-20 text-center',
      value: (placement) =>
        placement.type === PlacementType.AdUnit ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!placement.secondaryId}
            aria-label={dashboardT('placements.previewUnitTag')}
            onClick={() => {
              options.onShowUnitTag(placement);
            }}
          >
            <Code2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : null,
    },
    {
      id: 'status',
      kind: DashboardDataTableColumnKind.Status,
      header: dashboardT('placements.status'),
      sortable: true,
      label: (placement) => resolvePlacementStatusLabel(placement.status),
      tone: (placement) => resolvePlacementStatusTone(placement.status),
    },
    {
      id: 'secondaryId',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.id'),
      headerClassName: 'w-32',
      className: 'w-32',
      value: (placement) => <PlacementIdCell value={placement.secondaryId} />,
    },
    {
      id: 'publishers',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.publisher'),
      headerClassName: 'w-52',
      className: 'w-52',
      value: (placement) => {
        const names = placement.publishers.map((publisher) => publisher.name);
        if (!names.length) return null;
        return (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="block truncate">{names[0]}</span>
            </TooltipTrigger>
            <TooltipContent>{names.join(', ')}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      id: 'deliveryType',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.deliveryColumn'),
      sortable: true,
      value: (placement) => placement.deliveryType,
    },
    {
      id: 'integrations',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.integrationsColumn'),
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center',
      value: (placement) => {
        const count =
          placement.demandIntegrationsCnt ?? placement.placementDemandIntegrations.length;
        return (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!count}
            onClick={() => {
              options.onShowIntegrations(placement);
            }}
          >
            {count}
          </Button>
        );
      },
    },
    {
      id: 'targeting',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.targeting'),
      headerClassName: 'w-24 text-center',
      className: 'w-24 text-center',
      value: (placement) => {
        const count = placement.targetingCnt ?? placement.targetingList.length;
        return (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!count}
            onClick={() => {
              options.onShowTargeting(placement);
            }}
          >
            {count}
          </Button>
        );
      },
    },
    {
      id: 'bidFloor',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.floor'),
      sortable: true,
      value: (placement) => formatCurrency(placement.bidFloor),
    },
    {
      id: 'pricing',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.pricing'),
      sortable: true,
      value: (placement) => formatPricing(placement),
    },
    {
      id: 'bidCache',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.bidCacheShort'),
      headerClassName: 'w-16 text-center',
      className: 'w-16 text-center',
      value: (placement) =>
        dashboardT(
          placement.bcAvEnabled || placement.bcVideoEnabled ? 'placements.yes' : 'placements.no',
        ),
    },
    {
      id: 'updatedOn',
      kind: DashboardDataTableColumnKind.Date,
      header: dashboardT('placements.updated'),
      headerClassName: 'w-28',
      className: 'w-28 whitespace-nowrap',
      sortable: true,
      value: (placement) => formatDisplayDate(placement.updatedOn),
    },
    {
      id: 'notes',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.notes'),
      headerClassName: 'w-16 text-center',
      className: 'w-16 text-center',
      value: (placement) =>
        placement.notes ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={dashboardT('placements.viewNotes')}
              >
                <Info className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-80 whitespace-normal">
              {placement.notes}
            </TooltipContent>
          </Tooltip>
        ) : null,
    },
    {
      id: 'endpoint',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('placements.endpoint'),
      hidden: !options.canOpenEndpointGenerator,
      headerClassName: 'w-20 text-center',
      className: 'w-20 text-center',
      value: (placement) => {
        const publisherId = placement.publishersIds[0] ?? placement.publishers[0]?.id;
        if (
          !options.canOpenEndpointGenerator ||
          placement.status !== PlacementStatus.Active ||
          placement.type === PlacementType.AdUnit ||
          !publisherId
        ) {
          return null;
        }

        return (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="text-primary" asChild>
                <Link
                  to="/supply/endpoint-generator"
                  search={{ publisherId, placementId: placement.id }}
                  aria-label={dashboardT('placements.openEndpointGenerator')}
                >
                  <Zap className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{dashboardT('placements.openEndpointGenerator')}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      cell: (placement) => <PlacementActions placement={placement} options={options} />,
    },
  ]);
}

function formatPricing(placement: Placement) {
  if (
    placement.pricingType === PlacementPricingType.RevenueShare &&
    placement.pricing !== undefined
  ) {
    return numberFormat({ style: 'percent', maximumFractionDigits: 2 }).format(placement.pricing);
  }
  if (placement.pricingType === PlacementPricingType.Cpm) return formatCurrency(placement.pricing);
  return dashboardT('placements.pricingInherited');
}
